import { ArrowPointDown, ArrowPointUp } from "assets/icons/Common";
import EmojiBlue from "assets/Misc/Wellness/Emojis_Blue.png";
import EmojiGreen from "assets/Misc/Wellness/Emojis_Green.png";
import EmojiRed from "assets/Misc/Wellness/Emojis_Red.png";
import EmojiYellow from "assets/Misc/Wellness/Emojis_Yellow.png";
import { Calendar } from "components/generic/Calendar";
import { Col, DynamicIcon, Row } from "components/primitives";
import { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { WidgetSettingFC } from "types/Setting";

import { WellbeingCheckState } from "./WellbeingCheck";

interface HistoryInfo {
  label: string;
  noMargin?: boolean;
  infoMessage?: string;
}

interface ZoneProps {
  label: string;
  expanded: boolean;
  onClicked: (label: string) => void;
  value: any;
  names: any;
  color?: string;
}

export const DropZoneConfig: { [key: string]: any } = {
  mon: { color: "#7FDFDB", label: "Mon", emoji: EmojiGreen },
  tue: { color: "#80A0B6", label: "Tue", emoji: EmojiBlue },
  wed: { color: "#FEE9A5", label: "Wed", emoji: EmojiYellow },
  thu: { color: "#FF9090", label: "Thu", emoji: EmojiRed },
  fri: { color: "red", label: "Fri", emoji: EmojiGreen },
};

export const ColorConfig: { [key: string]: any } = {
  green: { color: "#7FDFDB", label: "Green", emoji: EmojiGreen },
  blue: { color: "#80A0B6", label: "Blue", emoji: EmojiBlue },
  yellow: { color: "#FEE9A5", label: "Yellow", emoji: EmojiYellow },
  red: { color: "#FF9090", label: "Red", emoji: EmojiRed },
};

export interface CheckInHistory {
  [date: string]: WellbeingCheckState;
}

const HistoryZone: FC<ZoneProps> = ({ expanded, label, onClicked, value, names, color }) => {
  const onClickZone = () => {
    onClicked(label);
  };

  return (
    <Col>
      <GroupHeader onClick={onClickZone}>
        <ColorDot color={ColorConfig[label]?.color} />
        {ColorConfig[label]?.label} ({names.length})
        <DropIcon Icon={expanded ? ArrowPointUp : ArrowPointDown} />
      </GroupHeader>
      <NameList height={expanded ? `${names.length * 2}em` : "0"}>
        {names.map((name: any, index: any) => (
          <p key={index}>{name}</p>
        ))}
      </NameList>
    </Col>
  );
};

const HistorySetting: WidgetSettingFC<CheckInHistory, HistoryInfo, WellbeingCheckState> = ({
  data,
  setData,
  info: { label, noMargin = false },
  state: { droppedNames },
}) => {
  const [history, setHistory] = useState<CheckInHistory | undefined>(data);

  // Calendar states
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [month, setMonth] = useState(selectedDate.getMonth());
  const [year, setYear] = useState(selectedDate.getFullYear());
  const [expandColor, setExpandColor] = useState<string[]>([]);

  const selectedDateString = selectedDate.toDateString();
  const activeHistory = history
    ? (history[selectedDateString] as WellbeingCheckState | undefined)?.droppedNames
    : {};

  const monthChanged = (month: number, year: number) => {
    setMonth(month);
    setYear(year);
  };

  useEffect(() => {
    const date = new Date().toDateString();
    setHistory((prevHistory) => ({
      ...prevHistory,
      [date]: { droppedNames } as WellbeingCheckState,
    }));
  }, [droppedNames]);

  useEffect(() => {
    function monthDiff(d1: Date, d2: Date) {
      var months;
      months = (d2.getFullYear() - d1.getFullYear()) * 12;
      months -= d1.getMonth();
      months += d2.getMonth();
      return months <= 0 ? 0 : months;
    }

    const cleanOldHistory = (history: CheckInHistory) => {
      const cleanHistory = { ...history };
      for (let key of Object.keys(history)) {
        const historyDate = new Date(key);
        const today = new Date();
        if (monthDiff(historyDate, today) > 1) {
          delete cleanHistory[key];
        }
      }

      return cleanHistory;
    };

    if (history) {
      const newHistory = cleanOldHistory(history);
      setData(newHistory);
    }
  }, [history]);

  const getCheckInDates = (): Date[] => {
    if (!history) return [];
    return Object.keys(history).map((date) => new Date(date));
  };

  return (
    <OuterContainer noMargin={noMargin}>
      <p className="label">{label}</p>
      <InnerContainer>
        <Calendar
          selectedDate={selectedDate}
          month={month}
          year={year}
          startOfWeek="Monday"
          colourPrevDays={true}
          dateSelected={setSelectedDate}
          monthChanged={monthChanged}
          highlightDates={getCheckInDates()}
        />
      </InnerContainer>
      <HistoryZoneContainer>
        {activeHistory ? (
          Object.entries(activeHistory).map(([key, val1]) => {
            return (
              <HistoryZone
                key={key}
                label={key}
                value={activeHistory}
                onClicked={(label) => {
                  if (expandColor.includes(label)) {
                    setExpandColor([]);
                  } else {
                    setExpandColor([label]);
                  }
                }}
                expanded={expandColor.includes(key)}
                names={Object.values(val1 as { [key: string]: any })}
                color={DropZoneConfig[label]?.color}
              />
            );
          })
        ) : (
          <p>No recorded check-ins for selected date</p>
        )}
      </HistoryZoneContainer>
    </OuterContainer>
  );
};

export default HistorySetting;

const OuterContainer = styled(Col)<{ noMargin: boolean }>`
  margin-bottom: ${(props) => (props.noMargin ? "0px" : "35px")};

  .label {
    margin-bottom: 8px;
    font-weight: 600;
    font-size: 12pt;
  }
`;

const InnerContainer = styled(Col)`
  gap: 15px;
`;

const HistoryZoneContainer = styled(InnerContainer)`
  margin-top: 16px;
`;

const GroupHeader = styled(Row)`
  width: 100%;
  height: 50px;
  border-radius: 5px;
  box-shadow: 0px 0px 6px 0px;
  padding: 3px 10px;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  background-color: white;

  &:hover {
    filter: brightness(95%);
  }
`;

const ColorDot = styled.div<{ color: string }>`
  border-radius: 50%;
  background-color: ${(props) => props.color};
  height: 50%;
  aspect-ratio: 1;
`;

const DropIcon = styled(DynamicIcon)`
  margin-left: auto;
  height: 40%;
  color: var(--primary);
`;

const NameList = styled.div<{ height: string }>`
  transition: all 0.5s ease;
  height: ${(props) => props.height};
  overflow: hidden;

  p {
    margin: 5px;
    line-height: 1.4;
    font-size: 12pt;
    font-weight: 600;
  }
`;
